import React, {
  useState,
  useContext,
  memo,
  useRef,
  useCallback,
  useEffect,
} from 'react';
import queryString from 'querystring';
import { AppContext } from '@prisjakt/ui-app-context';
import { useLocation } from 'react-router';
import * as S from './styles';

interface SearchBarProps {
  initialQuery?: string;
  placeholderText?: string;
  autoFocus?: boolean;
  autoFocusOnExpand?: boolean;
  focused?: boolean;
  className?: string;
  onFocusChange?: () => void;
  onInputChange?: (text: string) => void;
  onClear?: () => void;
  selected?: number;
  onBlurChange?: () => void;
}

type Query = string | string[];

const SearchBar = (props: SearchBarProps) => {
  const {
    onInputChange,
    className,
    autoFocusOnExpand,
    autoFocus,
    focused,
    onFocusChange,
    selected,
    onBlurChange,
    onClear,
  } = props;

  const { t } = useContext(AppContext);
  const { search } = useLocation();
  const searchInputEl = useRef<HTMLInputElement>(null);
  const getQuery = useCallback((): Query => {
    const qs = queryString.parse(search.substr(1));

    return qs.search || qs.q || '';
  }, [search]);
  const [searchValue, setSearchValue] = useState<Query>('');

  useEffect(() => {
    setSearchValue(getQuery());
  }, [search, getQuery]);

  const focusInput = (): void => {
    if (searchInputEl.current) {
      searchInputEl.current.focus();
    }
  };

  const handleClearSearchClick = useCallback(
    (event: React.MouseEvent): void => {
      event.stopPropagation();
      setSearchValue('');
      if (onClear) {
        onClear();
      }
      focusInput();
    },
    [onClear]
  );

  const handleSearchInputFocus = useCallback(() => {
    if (onFocusChange) {
      onFocusChange();
    }
  }, [onFocusChange]);

  const handleSearchInputBlur = useCallback(() => {
    if (onBlurChange) {
      onBlurChange();
    }
  }, [onBlurChange]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const searchInputValue = event.target.value;
      if (onInputChange) {
        onInputChange(searchInputValue);
      }
      setSearchValue(searchInputValue);
    },
    [onInputChange]
  );

  return (
    <S.SearchBar data-searchbar="true" className={className}>
      <S.InputWrapper>
        <S.SearchInput
          ref={searchInputEl}
          value={searchValue}
          data-suggestions="search"
          autoFocus={autoFocus || autoFocusOnExpand}
          aria-expanded={focused}
          aria-owns="overlaySearchResults"
          aria-autocomplete="both"
          aria-activedescendant={selected ? `selection${selected}` : ''}
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          type="search"
          placeholder={t('search', 'search.placeholder')}
          onFocus={handleSearchInputFocus}
          onBlur={handleSearchInputBlur}
          onChange={handleChange}
        />
        <S.ClearIcon
          show={!!searchValue}
          type="button"
          data-testid="clear-search-button"
          onClick={handleClearSearchClick}
        >
          <S.Close size={20} />
        </S.ClearIcon>
      </S.InputWrapper>
    </S.SearchBar>
  );
};

export default memo(SearchBar);
